if (!customElements.get('quantity-breaks')) {
  customElements.define(
    'quantity-breaks',
    class QuantityBreaks extends HTMLElement {
      connectedCallback() {
        this.sectionId = this.dataset.sectionId;
        this.quantityInput = document.getElementById(`Quantity-${this.sectionId}`);
        this.submitButton = document.getElementById(`ProductSubmitButton-${this.sectionId}`);
        this.submitButtonText = this.submitButton?.querySelector('span');
        this.defaultButtonText = this.submitButtonText?.textContent.trim() || '';
        this.bindControls();

        this.variantChangeUnsubscriber = subscribe(PUB_SUB_EVENTS.variantChange, (event) => {
          if (event.data.sectionId !== this.sectionId) return;
          if (event.data.variant?.price != null) {
            this.dataset.regularUnitCents = String(event.data.variant.price);
          }
          if (event.data.variant?.id != null) this.dataset.variantId = String(event.data.variant.id);
          this.updateDisplay(this.currentQuantity());
        });

        this.updateDisplay(this.currentQuantity());
      }

      disconnectedCallback() {
        if (this.variantChangeUnsubscriber) this.variantChangeUnsubscriber();
      }

      bindControls() {
        this.querySelectorAll('.quantity-breaks__radio').forEach((radio) => {
          radio.addEventListener('change', () => {
            if (!radio.checked) return;
            const quantity = Number.parseInt(radio.value, 10);
            this.setQuantity(quantity);
            window.dataLayer = window.dataLayer || [];
            window.dataLayer.push({
              event: 'bundle_option_selected',
              product_id: this.dataset.productId,
              variant_id: this.dataset.variantId,
              bundle_quantity: quantity,
              bundle_offer_type: quantity > 1 ? this.dataset.offerType : 'single'
            });
          });
        });

        this.quantityInput?.addEventListener('change', () => this.updateDisplay(this.currentQuantity()));
        this.quantityInput?.addEventListener('input', () => this.updateDisplay(this.currentQuantity()));
      }

      currentQuantity() {
        const value = Number.parseInt(this.quantityInput?.value || '1', 10);
        return Number.isFinite(value) && value > 0 ? value : 1;
      }

      setQuantity(quantity) {
        if (!this.quantityInput) return;
        this.quantityInput.value = String(quantity);
        this.quantityInput.dispatchEvent(new Event('input', { bubbles: true }));
        this.quantityInput.dispatchEvent(new Event('change', { bubbles: true }));
        this.updateDisplay(quantity);
      }

      updateDisplay(quantity) {
        const minQuantity = Number.parseInt(this.dataset.minQuantity || '0', 10);
        const regularUnit = Number.parseInt(this.dataset.regularUnitCents || '0', 10);
        const offerPrice = Number.parseInt(this.dataset.offerPriceCents || '0', 10);
        const discountPercent = Number.parseFloat(this.dataset.offerDiscountPercent || '0');
        const maxSets = Number.parseInt(this.dataset.maxSets || '0', 10);
        const offerType = this.dataset.offerType;
        const isPercentage = offerType === 'percentage' && discountPercent > 0 && discountPercent < 100;

        if (!minQuantity || !regularUnit || (!offerPrice && !isPercentage)) return;

        const singleRadio = this.querySelector('.quantity-breaks__radio[value="1"]');
        const offerRadio = this.querySelector(`.quantity-breaks__radio[value="${minQuantity}"]`);
        const qualifies = quantity >= minQuantity;

        if (isPercentage) {
          const discountedUnit = Math.round(regularUnit * (100 - discountPercent) / 100);
          const total = qualifies ? discountedUnit * quantity : regularUnit * quantity;
          const offerSavings = (regularUnit - discountedUnit) * minQuantity;

          if (offerRadio) offerRadio.checked = qualifies;
          if (singleRadio) singleRadio.checked = !qualifies && quantity === 1;

          this.setMoney('[data-bundle-unit]', discountedUnit);
          this.setMoney('[data-bundle-total]', discountedUnit * minQuantity);
          this.setMoney('[data-bundle-saving]', Math.max(offerSavings, 0));
          this.updateStatus(quantity, total, qualifies);
          return;
        }

        if (offerType === 'unit_price') {
          if (offerRadio) offerRadio.checked = qualifies;
          if (singleRadio) singleRadio.checked = !qualifies && quantity === 1;

          const total = qualifies ? offerPrice * quantity : regularUnit * quantity;
          const offerSavings = (regularUnit - offerPrice) * minQuantity;
          this.setMoney('[data-bundle-unit]', offerPrice);
          this.setMoney('[data-bundle-total]', offerPrice * minQuantity);
          this.setMoney('[data-bundle-saving]', Math.max(offerSavings, 0));
          this.updateStatus(quantity, total, qualifies);
          return;
        }

        const possibleSets = Math.floor(quantity / minQuantity);
        const appliedSets = maxSets > 0 ? Math.min(possibleSets, maxSets) : possibleSets;
        const discountedItems = appliedSets * minQuantity;
        const remainingItems = quantity - discountedItems;
        const total = appliedSets * offerPrice + remainingItems * regularUnit;
        const offerSavings = regularUnit * minQuantity - offerPrice;

        if (offerRadio) offerRadio.checked = quantity === minQuantity;
        if (singleRadio) singleRadio.checked = quantity === 1;

        this.setMoney('[data-bundle-unit]', Math.round(offerPrice / minQuantity));
        this.setMoney('[data-bundle-total]', offerPrice);
        this.setMoney('[data-bundle-saving]', Math.max(offerSavings, 0));
        this.updateStatus(quantity, total, appliedSets > 0);
      }

      updateStatus(quantity, total, qualifies) {
        const status = this.querySelector('[data-bundle-status]');
        if (status) {
          status.textContent = qualifies ? `${quantity} 件優惠價：${this.formatMoney(total)}` : '';
        }

        if (!this.submitButtonText || this.submitButton?.disabled) return;
        this.submitButtonText.textContent = qualifies
          ? `加入 ${quantity} 件 · ${this.formatMoney(total)}`
          : this.defaultButtonText;
      }

      setMoney(selector, cents) {
        const node = this.querySelector(selector);
        if (node) node.textContent = this.formatMoney(cents);
      }

      formatMoney(cents) {
        const format = this.dataset.moneyFormat || '${{amount}}';
        const placeholder = format.match(/\{\{\s*(\w+)\s*\}\}/);
        if (!placeholder) return (cents / 100).toFixed(2);

        const withDelimiters = (number, precision, thousands, decimal) => {
          const fixed = (number / 100).toFixed(precision);
          const parts = fixed.split('.');
          parts[0] = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, thousands);
          return parts[1] ? `${parts[0]}${decimal}${parts[1]}` : parts[0];
        };

        const values = {
          amount: withDelimiters(cents, 2, ',', '.'),
          amount_no_decimals: withDelimiters(cents, 0, ',', '.'),
          amount_with_comma_separator: withDelimiters(cents, 2, '.', ','),
          amount_no_decimals_with_comma_separator: withDelimiters(cents, 0, '.', ','),
        };

        return format.replace(placeholder[0], values[placeholder[1]] ?? values.amount);
      }
    }
  );
}
